// Phase 9.1 — Item detail page.
//
// Route: /tools/fnb-po/items/:sku
// 4 cards: header, current stock (per-location), thresholds (sheet
// vs calculated placeholder), vendor rates. Reorder history is
// intentionally empty in 9.1 (comes with the second snapshot).

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusPill, fmtNum } from "./statusMeta";
import { ApplicabilityPicker } from "./ApplicabilityPicker";
import { VendorPicker } from "@/components/fnb/VendorPicker";

const EMPTY_FORM = Object.freeze({
  vendor_name: "",
  vendor_id: null,
  reorder_level_qty: "",
  standard_order_qty: "",
  standard_order_unit: "",
});

export default function FnbPoItemDetail() {
  const { sku } = useParams();
  const [detail, setDetail] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingApplicability, setSavingApplicability] = useState(false);
  // Phase 9.8 — Vendor & Reorder config form state.
  const [orderingCfg, setOrderingCfg] = useState(null);   // server row or null
  const [cfgFormLoading, setCfgFormLoading] = useState(true);
  const [cfgForm, setCfgForm] = useState(EMPTY_FORM);
  const [cfgFormBaseline, setCfgFormBaseline] = useState(EMPTY_FORM);
  const [cfgFormErrors, setCfgFormErrors] = useState({});
  const [cfgSaving, setCfgSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detailRes, locsRes] = await Promise.all([
        api.get(`/fnb/items/${encodeURIComponent(sku)}`),
        api.get(`/fnb/locations`),
      ]);
      setDetail(detailRes.data);
      setLocations((locsRes.data || []).filter((l) => l.active !== false));
    } catch (e) {
      const msg = detailFromError(e);
      setError(msg);
      if (e?.response?.status !== 404) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [sku]);

  useEffect(() => { load(); }, [load]);

  // Fetch the existing fb_item_ordering_config row for this SKU (if any).
  // 404 means "no config row yet" — leave the form blank.
  const loadOrderingCfg = useCallback(async () => {
    setCfgFormLoading(true);
    try {
      const res = await api.get(
        `/fnb/item-ordering-config/${encodeURIComponent(sku)}`,
      );
      const row = res.data;
      setOrderingCfg(row);
      const initial = {
        vendor_name: row?.vendor_name || "",
        vendor_id: null, // filled reactively once vendors are loaded
        reorder_level_qty:
          row?.reorder_level_qty != null ? String(row.reorder_level_qty) : "",
        standard_order_qty:
          row?.standard_order_qty != null ? String(row.standard_order_qty) : "",
        standard_order_unit: row?.standard_order_unit || "",
      };
      setCfgForm(initial);
      setCfgFormBaseline(initial);
    } catch (e) {
      if (e?.response?.status === 404) {
        // Empty state — Bucket-B SKU with no config row yet.
        setOrderingCfg(null);
        setCfgForm(EMPTY_FORM);
        setCfgFormBaseline(EMPTY_FORM);
      } else {
        toast.error(`Failed to load reorder config: ${detailFromError(e)}`);
      }
    } finally {
      setCfgFormLoading(false);
    }
  }, [sku]);

  useEffect(() => { loadOrderingCfg(); }, [loadOrderingCfg]);

  const saveApplicability = useCallback(async (ids) => {
    setSavingApplicability(true);
    try {
      const body = ids === null
        ? { clear_applicability: true }
        : { applicable_location_ids: ids };
      await api.patch(`/fnb/items/${encodeURIComponent(sku)}`, body);
      toast.success("Applicability saved");
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setSavingApplicability(false);
    }
  }, [sku, load]);

  // Which fields in the vendor/reorder form have changed vs baseline?
  const cfgFieldsDirty = useMemo(() => {
    const dirty = {};
    for (const k of Object.keys(EMPTY_FORM)) {
      if (k === "vendor_id") continue; // derived, not user-typed
      if ((cfgForm[k] ?? "") !== (cfgFormBaseline[k] ?? "")) dirty[k] = true;
    }
    return dirty;
  }, [cfgForm, cfgFormBaseline]);
  const cfgIsDirty = Object.keys(cfgFieldsDirty).length > 0;

  const handleVendorPick = useCallback(({ name, id }) => {
    setCfgForm((prev) => ({ ...prev, vendor_name: name || "", vendor_id: id }));
    setCfgFormErrors((prev) => ({ ...prev, vendor_name: undefined }));
  }, []);

  const handleCfgField = (key) => (e) => {
    const v = e?.target?.value ?? "";
    setCfgForm((prev) => ({ ...prev, [key]: v }));
    setCfgFormErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateCfg = useCallback(() => {
    const errs = {};
    const anyReorderField =
      cfgForm.reorder_level_qty !== "" ||
      cfgForm.standard_order_qty !== "" ||
      cfgForm.standard_order_unit !== "";
    if (anyReorderField && !cfgForm.vendor_name) {
      errs.vendor_name = "Vendor is required when any reorder field is set";
    }
    for (const key of ["reorder_level_qty", "standard_order_qty"]) {
      const raw = cfgForm[key];
      if (raw === "" || raw === null || raw === undefined) continue;
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 0) {
        errs[key] = "Must be a number ≥ 0";
      }
    }
    setCfgFormErrors(errs);
    return Object.keys(errs).length === 0;
  }, [cfgForm]);

  const saveVendorReorderConfig = useCallback(async () => {
    if (!validateCfg()) return;
    if (!cfgIsDirty) {
      toast.info("Nothing to save — no fields changed.");
      return;
    }
    setCfgSaving(true);
    try {
      // Build the config PATCH payload — only include DIRTY fields.
      // Any PATCH flips mapping_source="manual" (backend semantics),
      // so we deliberately avoid sending unchanged fields.
      const cfgPatch = {};
      if (cfgFieldsDirty.vendor_name) cfgPatch.vendor_name = cfgForm.vendor_name;
      if (cfgFieldsDirty.reorder_level_qty)
        cfgPatch.reorder_level_qty = Number(cfgForm.reorder_level_qty);
      if (cfgFieldsDirty.standard_order_qty)
        cfgPatch.standard_order_qty = Number(cfgForm.standard_order_qty);
      if (cfgFieldsDirty.standard_order_unit)
        cfgPatch.standard_order_unit = cfgForm.standard_order_unit;
      const configCall = api.patch(
        `/fnb/item-ordering-config/${encodeURIComponent(sku)}`,
        cfgPatch,
      );
      // Applicability is already handled by its own save handler on the
      // ApplicabilityPicker (unchanged). This modal doesn't couple to it.
      // Fire the config call standalone.
      const results = await Promise.allSettled([configCall]);
      const cfgRes = results[0];
      if (cfgRes.status === "fulfilled") {
        toast.success("Vendor & reorder config saved (manual-locked)");
        await Promise.all([load(), loadOrderingCfg()]);
      } else {
        const status = cfgRes.reason?.response?.status;
        if (status === 404) {
          toast.error(
            "No reorder-config row exists for this SKU yet. " +
            "Create one via the CSV round-trip on the Ordering Rules " +
            "page, then edit it here.",
          );
        } else {
          toast.error(
            `Failed to save reorder config: ${detailFromError(cfgRes.reason)}`,
          );
        }
      }
    } finally {
      setCfgSaving(false);
    }
  }, [
    cfgIsDirty, cfgFieldsDirty, cfgForm, sku,
    validateCfg, load, loadOrderingCfg,
  ]);

  if (loading) {
    return (
      <div className="text-sm text-muted-foreground" data-testid="item-detail-loading">
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-4" data-testid="item-detail-error">
        <Alert variant="destructive">
          <AlertTitle>Item not found</AlertTitle>
          <AlertDescription>
            SKU <code className="font-mono">{sku}</code>{" "}
            doesn&apos;t exist in the current master data.
          </AlertDescription>
        </Alert>
        <Button asChild variant="ghost" size="sm">
          <Link to="/tools/fnb-po/reorder">← Back to reorder dashboard</Link>
        </Button>
      </div>
    );
  }

  if (!detail) return null;
  const { item, company_wide, by_location, sheet_levels, vendor_rates } = detail;

  return (
    <div className="space-y-6" data-testid={`item-detail-${sku}`}>
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight" data-testid="item-name">
              {item.name || "Unnamed item"}
            </h1>
            <StatusPill status={company_wide.status} testid="item-company-status" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <code className="font-mono">{item.sku}</code>
            {item.type && <Badge variant="outline" className="text-[10px]">{item.type}</Badge>}
            {item.category && (
              <Badge variant="secondary" className="text-[10px]">{item.category}</Badge>
            )}
            {item.sub_category && (
              <Badge variant="outline" className="text-[10px]">{item.sub_category}</Badge>
            )}
            {item.uom && (
              <span>UOM: <code className="font-mono">{item.uom}</code></span>
            )}
            {item.is_placeholder_sku && (
              <Badge variant="outline" className="text-[10px] text-amber-800 border-amber-300 bg-amber-50">
                Placeholder SKU
              </Badge>
            )}
          </div>
        </div>
        <Button asChild variant="ghost" size="sm" data-testid="item-back">
          <Link to="/tools/fnb-po/reorder">← Reorder dashboard</Link>
        </Button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Card 1: Current stock breakdown */}
        <Card data-testid="item-stock-card">
          <CardHeader>
            <CardTitle className="text-base">Current stock by location</CardTitle>
            <CardDescription className="text-xs">
              Company-wide total: <b>{fmtNum(company_wide.current_stock_total)}</b> · Snapshot from{" "}
              {detail.snapshot_uploaded_at
                ? new Date(detail.snapshot_uploaded_at).toLocaleDateString()
                : "—"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {by_location.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                No location data. Upload a stock CSV first.
              </div>
            ) : (
              <Table data-testid="item-stock-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {by_location.map((row) => (
                    <TableRow
                      key={row.location_id}
                      data-testid={`item-loc-row-${row.location_id}`}
                      className={row.status === "not_applicable" ? "opacity-60" : ""}
                    >
                      <TableCell className="text-sm">
                        <div>{row.display_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {row.location_id}
                          {row.is_warehouse && (
                            <Badge variant="outline" className="ml-1 text-[9px]">warehouse</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {row.status === "not_applicable"
                          ? <span className="text-muted-foreground italic">—</span>
                          : fmtNum(row.qty)}
                      </TableCell>
                      <TableCell>
                        <StatusPill status={row.status} testid={`item-loc-status-${row.location_id}`} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Card 2: Thresholds */}
        <Card data-testid="item-thresholds-card">
          <CardHeader>
            <CardTitle className="text-base">Reorder thresholds</CardTitle>
            <CardDescription className="text-xs">
              Sheet levels from the vendor workbook · Calculated levels from
              6+ months of POS sales via BOM mapping (Phase 9.3).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table data-testid="item-thresholds-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Level</TableHead>
                  <TableHead className="text-right">Sheet-defined</TableHead>
                  <TableHead className="text-right">
                    Calculated
                    {detail.calculated_levels?.coverage_pct != null && !detail.calculated_levels?.no_bom && (
                      <span
                        className={`ml-1 text-[9px] px-1 py-0.5 rounded border ${
                          detail.calculated_levels.insufficient_data
                            ? "border-amber-300 bg-amber-50 text-amber-900"
                            : "border-emerald-300 bg-emerald-50 text-emerald-900"
                        }`}
                        title={detail.calculated_levels.reason || ""}
                        data-testid="coverage-chip"
                      >
                        {detail.calculated_levels.coverage_pct}% coverage
                      </span>
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="text-sm">Reorder level</TableCell>
                  <TableCell className="text-right text-sm">{fmtNum(sheet_levels.reorder)}</TableCell>
                  <TableCell className="text-right text-sm" data-testid="calc-reorder">
                    {detail.calculated_levels?.reorder != null
                      ? fmtNum(detail.calculated_levels.reorder)
                      : <span className="text-muted-foreground italic">—</span>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-sm">Danger level</TableCell>
                  <TableCell className="text-right text-sm">{fmtNum(sheet_levels.danger)}</TableCell>
                  <TableCell className="text-right text-sm" data-testid="calc-danger">
                    {detail.calculated_levels?.danger != null
                      ? fmtNum(detail.calculated_levels.danger)
                      : <span className="text-muted-foreground italic">—</span>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-sm">Max level</TableCell>
                  <TableCell className="text-right text-sm">{fmtNum(sheet_levels.max)}</TableCell>
                  <TableCell className="text-right text-sm" data-testid="calc-max">
                    {detail.calculated_levels?.max != null
                      ? fmtNum(detail.calculated_levels.max)
                      : <span className="text-muted-foreground italic">—</span>}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="text-sm">TAT (days)</TableCell>
                  <TableCell className="text-right text-sm">{fmtNum(sheet_levels.tat_days)}</TableCell>
                  <TableCell className="text-right text-sm">
                    {detail.calculated_levels?.tat_days != null
                      ? fmtNum(detail.calculated_levels.tat_days)
                      : <span className="text-muted-foreground italic">—</span>}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <div className="text-[11px] text-muted-foreground pt-3 border-t border-border/50 mt-3 space-y-1">
              {detail.calculated_levels?.no_bom && (
                <div data-testid="no-bom-hint">
                  No consumption data yet — this item isn&apos;t referenced by any
                  verified BOM.{" "}
                  <Link
                    to={`/tools/fnb-po/bom?search=${encodeURIComponent(item.name || item.sku)}`}
                    className="text-primary underline underline-offset-4"
                  >
                    Connect via BOM mapping →
                  </Link>
                </div>
              )}
              {!detail.calculated_levels?.no_bom && detail.calculated_levels?.insufficient_data && (
                <div className="text-amber-800" data-testid="insufficient-data-hint">
                  Data below the confidence gate: <b>{detail.calculated_levels.reason}</b>.
                  Levels stay at sheet-defined values.
                </div>
              )}
              {detail.calculated_levels?.reorder != null && (
                <div>
                  ADC: <b>{fmtNum(detail.calculated_levels.avg_daily_consumption)}</b>/day ·{" "}
                  MDC: <b>{fmtNum(detail.calculated_levels.max_daily_consumption)}</b>/day ·{" "}
                  {detail.calculated_levels.days_with_nonzero} non-zero days
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Vendor rates */}
        <Card data-testid="item-vendor-rates-card">
          <CardHeader>
            <CardTitle className="text-base">Vendor rates</CardTitle>
            <CardDescription className="text-xs">
              Rates sourced from the vendor-rate workbook.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {vendor_rates.length === 0 ? (
              <div className="text-xs text-muted-foreground" data-testid="item-vendor-rates-empty">
                No vendor rates on file for this item.
              </div>
            ) : (
              <Table data-testid="item-vendor-rates-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendor</TableHead>
                    <TableHead className="text-right">Unit rate (₹)</TableHead>
                    <TableHead>UOM</TableHead>
                    <TableHead className="text-right">TAT (d)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendor_rates.map((v, i) => (
                    <TableRow key={`${v.vendor_id}-${i}`} data-testid={`item-vendor-rate-row-${v.vendor_id}`}>
                      <TableCell className="text-sm">
                        <div>{v.vendor_name || v.vendor_id}</div>
                        <div className="text-[10px] text-muted-foreground font-mono">
                          {v.vendor_id}
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">
                        {fmtNum(v.unit_rate)}
                      </TableCell>
                      <TableCell className="text-sm">{v.uom || "—"}</TableCell>
                      <TableCell className="text-right text-sm">{fmtNum(v.tat_days)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Card 4: Reorder history (empty in 9.1) */}
        <Card data-testid="item-history-card">
          <CardHeader>
            <CardTitle className="text-base">Recent status changes</CardTitle>
            <CardDescription className="text-xs">
              Populated automatically once a second stock snapshot is uploaded.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className="text-xs text-muted-foreground py-4 text-center border border-dashed border-border/60 rounded-md"
              data-testid="item-history-empty"
            >
              No changes yet — only one stock snapshot on file.
            </div>
          </CardContent>
        </Card>

        {/* Card 5: Applicable locations (Phase 9.1.1) */}
        <Card data-testid="item-applicability-card" className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">Applicable locations</CardTitle>
            <CardDescription className="text-xs">
              Restrict this item to the outlets where it&apos;s sold or stocked. Non-applicable locations
              show &quot;N/A&quot; instead of &quot;Out of stock&quot; on the reorder dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ApplicabilityPicker
              initialApplicable={item.applicable_location_ids ?? null}
              activeLocations={locations}
              onSave={saveApplicability}
              saving={savingApplicability}
              testidPrefix="item-applicability"
            />
          </CardContent>
        </Card>

        {/* Card 6: Vendor & Reorder Config (Phase 9.8) */}
        <Card
          data-testid="item-vendor-reorder-card"
          className="lg:col-span-2"
        >
          <CardHeader>
            <CardTitle className="text-base">Vendor &amp; Reorder Config</CardTitle>
            <CardDescription className="text-xs">
              The vendor and reorder rule used by the Stock Matrix and Reorder
              dashboard. Saving here manual-locks the row so xlsx re-uploads
              won&apos;t overwrite it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {cfgFormLoading ? (
              <div
                className="text-xs text-muted-foreground"
                data-testid="item-vendor-reorder-loading"
              >
                Loading…
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label
                      htmlFor="item-cfg-vendor"
                      className="text-xs font-medium"
                    >
                      Vendor
                      {(cfgForm.reorder_level_qty !== "" ||
                        cfgForm.standard_order_qty !== "" ||
                        cfgForm.standard_order_unit !== "") && (
                        <span className="text-destructive"> *</span>
                      )}
                    </Label>
                    <div data-testid="item-cfg-vendor-wrap">
                      <VendorPicker
                        value={cfgForm.vendor_name || null}
                        onChange={handleVendorPick}
                        placeholder="Select vendor…"
                        disabled={cfgSaving}
                      />
                    </div>
                    {cfgFormErrors.vendor_name && (
                      <div
                        className="text-[11px] text-destructive"
                        data-testid="item-cfg-vendor-error"
                      >
                        {cfgFormErrors.vendor_name}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="item-cfg-ro-qty"
                      className="text-xs font-medium"
                    >
                      Reorder level qty
                    </Label>
                    <Input
                      id="item-cfg-ro-qty"
                      type="number"
                      min={0}
                      inputMode="decimal"
                      value={cfgForm.reorder_level_qty}
                      onChange={handleCfgField("reorder_level_qty")}
                      disabled={cfgSaving}
                      data-testid="item-cfg-ro-qty"
                    />
                    {cfgFormErrors.reorder_level_qty && (
                      <div
                        className="text-[11px] text-destructive"
                        data-testid="item-cfg-ro-qty-error"
                      >
                        {cfgFormErrors.reorder_level_qty}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="item-cfg-sto-qty"
                      className="text-xs font-medium"
                    >
                      Standard order qty
                    </Label>
                    <Input
                      id="item-cfg-sto-qty"
                      type="number"
                      min={0}
                      inputMode="decimal"
                      value={cfgForm.standard_order_qty}
                      onChange={handleCfgField("standard_order_qty")}
                      disabled={cfgSaving}
                      data-testid="item-cfg-sto-qty"
                    />
                    {cfgFormErrors.standard_order_qty && (
                      <div
                        className="text-[11px] text-destructive"
                        data-testid="item-cfg-sto-qty-error"
                      >
                        {cfgFormErrors.standard_order_qty}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label
                      htmlFor="item-cfg-sto-unit"
                      className="text-xs font-medium"
                    >
                      Standard order unit
                    </Label>
                    <Input
                      id="item-cfg-sto-unit"
                      type="text"
                      value={cfgForm.standard_order_unit}
                      onChange={handleCfgField("standard_order_unit")}
                      disabled={cfgSaving}
                      placeholder="e.g. kg, case, pkt"
                      data-testid="item-cfg-sto-unit"
                    />
                  </div>
                </div>

                {/* Last known rate — read-only reference */}
                <div
                  className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs"
                  data-testid="item-last-known-rate"
                >
                  {(() => {
                    const pickedId = cfgForm.vendor_id;
                    const pickedName = cfgForm.vendor_name;
                    const match = (vendor_rates || []).find((r) =>
                      pickedId
                        ? r.vendor_id === pickedId
                        : (r.vendor_name || "").toLowerCase() ===
                          (pickedName || "").toLowerCase(),
                    );
                    if (!pickedName) {
                      return (
                        <span className="text-muted-foreground">
                          Select a vendor to see the last known rate.
                        </span>
                      );
                    }
                    if (!match) {
                      return (
                        <>
                          <div>
                            <b>Last known rate:</b>{" "}
                            <span className="font-mono">—</span>
                          </div>
                          <div className="text-muted-foreground text-[11px] mt-0.5">
                            No rate on file for {pickedName}. Rate updates
                            automatically from your most recent PO for this
                            item + vendor.
                          </div>
                        </>
                      );
                    }
                    return (
                      <>
                        <div>
                          <b>Last known rate:</b>{" "}
                          <span className="font-mono">
                            ₹{fmtNum(match.unit_rate)}
                          </span>
                          {match.uom ? ` per ${match.uom}` : ""}
                        </div>
                        <div className="text-muted-foreground text-[11px] mt-0.5">
                          Rate updates automatically from your most recent PO
                          for this item + vendor.
                        </div>
                      </>
                    );
                  })()}
                </div>

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    onClick={saveVendorReorderConfig}
                    disabled={cfgSaving || !cfgIsDirty}
                    data-testid="item-cfg-save-btn"
                  >
                    {cfgSaving ? "Saving…" : "Save Vendor & Reorder Config"}
                  </Button>
                  {orderingCfg?.mapping_source && (
                    <Badge
                      variant="outline"
                      className="text-[10px]"
                      data-testid="item-cfg-mapping-source"
                    >
                      mapping_source: {orderingCfg.mapping_source}
                    </Badge>
                  )}
                  {!cfgIsDirty && !cfgSaving && (
                    <span
                      className="text-[11px] text-muted-foreground"
                      data-testid="item-cfg-clean"
                    >
                      No unsaved changes
                    </span>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
